import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
    ComposableMap,
    Geographies,
    Geography,
    Marker,
    ZoomableGroup
} from "react-simple-maps";

// World map GeoJSON URL (Updated to a working CDN link)
const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// ISO 3166-1 alpha-2 -> full name for CEO-friendly display
const COUNTRY_NAMES = {
    US: 'United States', CA: 'Canada', GB: 'United Kingdom', FR: 'France', DE: 'Germany',
    JP: 'Japan', AU: 'Australia', IN: 'India', BR: 'Brazil', CN: 'China', IT: 'Italy',
    ES: 'Spain', NL: 'Netherlands', PL: 'Poland', RU: 'Russia', UA: 'Ukraine', TR: 'Turkey',
    KR: 'South Korea', MX: 'Mexico', AR: 'Argentina', ZA: 'South Africa', NG: 'Nigeria',
    EG: 'Egypt', SA: 'Saudi Arabia', AE: 'United Arab Emirates', HR: 'Croatia', SI: 'Slovenia',
    RS: 'Serbia', BA: 'Bosnia and Herzegovina', ME: 'Montenegro', MK: 'North Macedonia',
    AL: 'Albania', GR: 'Greece', PT: 'Portugal', SE: 'Sweden', NO: 'Norway', DK: 'Denmark',
    FI: 'Finland', IE: 'Ireland', CH: 'Switzerland', AT: 'Austria', BE: 'Belgium',
    CZ: 'Czech Republic', RO: 'Romania', HU: 'Hungary',
    Unknown: 'Unknown Location', 'Local (Dev)': 'Local (Dev)'
};

// Fallback country code -> [longitude, latitude] for map when API has no lat/lng (e.g. old records)
const COUNTRY_COORDS = {
    US: [-98, 38], CA: [-106, 56], GB: [-2, 54], FR: [2, 46], DE: [10, 51],
    JP: [138, 36], AU: [133, -27], IN: [77, 20], BR: [-51, -14], CN: [104, 35],
    IT: [12, 43], ES: [-3, 40], NL: [5, 52], PL: [19, 52], RU: [105, 55],
    UA: [31, 49], TR: [35, 39], KR: [128, 36], MX: [-102, 23], AR: [-64, -34],
    ZA: [25, -29], NG: [8, 10], EG: [30, 27], SA: [45, 24], AE: [54, 24],
    HR: [15.2, 45.1], SI: [14.8, 46], RS: [21, 44], BA: [17.8, 44], ME: [19.3, 43],
    MK: [21.7, 41.5], AL: [20, 41], GR: [22, 39], PT: [-8, 39], SE: [15, 62],
    NO: [9, 62], DK: [10, 56], FI: [26, 64], IE: [-8, 53], CH: [8, 47],
    AT: [14, 47], BE: [4.5, 50.5], CZ: [15, 50], RO: [25, 46], HU: [19, 47],
    Unknown: [0, 20], 'Local (Dev)': [0, 20] // Shown in Atlantic so map has a marker
};

const getCoords = (countryCode) => {
    if (!countryCode) return COUNTRY_COORDS.Unknown;
    return COUNTRY_COORDS[countryCode] ?? COUNTRY_COORDS.Unknown;
};

const getCountryDisplayName = (countryCode) => {
    return COUNTRY_NAMES[countryCode] ?? countryCode;
};

const AnalyticsDisplay = ({ analyticsData }) => {
    const { t } = useTranslation();
    console.log(`[${new Date().toISOString()}] AnalyticsDisplay render`, {
        hasData: !!analyticsData,
        locationCount: analyticsData?.locationData?.length ?? 0
    });

    // --- Theming ---
    const textPrimary = "text-silver-light";
    const textSecondary = "text-silver-medium/80";
    const accentColor = "text-blue-400";
    const contentBg = "bg-gray-950/50";
    const borderColor = "border-gray-700/30";

    const totalHits = analyticsData?.locationData?.reduce((sum, loc) => sum + loc.count, 0) ?? 0;
    const geoLocations = analyticsData?.locationData?.filter(l => l.country !== 'Unknown' && l.country !== 'Local (Dev)') ?? [];
    const hasOnlyNonGeoLocations = analyticsData?.locationData?.length > 0 && geoLocations.length === 0;

    // Build markers only for real geographic locations; skip Unknown/Local so we don't show a single dot in the ocean
    const markers = useMemo(() => {
        if (!analyticsData?.locationData?.length || hasOnlyNonGeoLocations) return [];
        return analyticsData.locationData
            .filter(loc => loc.country !== 'Unknown' && loc.country !== 'Local (Dev)')
            .map((loc, idx) => {
                const name = loc.country || 'Unknown';
                const hasApiCoords = typeof loc.lng === 'number' && typeof loc.lat === 'number';
                const coords = hasApiCoords ? [loc.lng, loc.lat] : getCoords(loc.country);
                return {
                    key: `${name}-${idx}-${loc.count}`,
                    markerOffset: -15,
                    name: getCountryDisplayName(name),
                    countryCode: name,
                    coordinates: coords,
                    count: loc.count
                };
            })
            .filter(m => m.coordinates[0] != null && m.coordinates[1] != null);
    }, [analyticsData?.locationData, hasOnlyNonGeoLocations]);

    if (!analyticsData) {
        return <p className={textSecondary}>No analytics data available.</p>;
    }

    const countryCount = geoLocations.length || analyticsData?.locationData?.length || 0;
    const countLabel = countryCount === 1
        ? t('developerPage.analyticsCountCountry')
        : t('developerPage.analyticsCountCountries');
    const headlineText = hasOnlyNonGeoLocations
        ? t('developerPage.analyticsSummaryHeadlineNoGeo', { total: totalHits })
        : t('developerPage.analyticsSummaryHeadline', { total: totalHits, count: countryCount, countLabel });

    return (
        <div className="space-y-8">
            {/* One-line narrative for leadership */}
            <p className={`text-center ${textSecondary} text-sm`}>{headlineText}</p>
            <p className={`text-center ${textSecondary} text-xs opacity-80`}>{t('developerPage.analyticsPrivacyNote')}</p>

            {/* Stats Section */}
            <div className={`${contentBg} p-5 rounded-lg border ${borderColor}`}>
                <h3 className={`text-lg font-medium mb-4 ${accentColor}`}>Key Metrics</h3>
                <dl className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                    <div className="sm:col-span-1 bg-gray-900/30 p-4 rounded-md border border-gray-800/20">
                        <dt className={`text-sm font-medium ${textSecondary}`}>Total Messages</dt>
                        <dd className={`mt-2 text-3xl font-semibold tracking-tight ${textPrimary}`}>
                            {analyticsData.totalMessages !== undefined ? analyticsData.totalMessages : 'N/A'}
                        </dd>
                    </div>
                    <div className="sm:col-span-1 bg-gray-900/30 p-4 rounded-md border border-gray-800/20">
                        <dt className={`text-sm font-medium ${textSecondary}`}>Total Hits (Last 30 Days)</dt>
                        <dd className={`mt-2 text-3xl font-semibold tracking-tight ${textPrimary}`}>
                            {analyticsData.locationData ? analyticsData.locationData.reduce((sum, loc) => sum + loc.count, 0) : 'N/A'}
                        </dd>
                    </div>
                </dl>
            </div>

            {/* Map and Location List Section */}
            <div className="flex flex-col md:flex-row gap-8">
                {/* Map Section */}
                <div className="flex-grow md:w-2/3">
                    <h3 className={`text-lg font-medium mb-4 ${accentColor} flex items-center`}>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        Visitor Locations (Last 30 Days)
                    </h3>
                    {(!analyticsData.locationData || analyticsData.locationData.length === 0) ? (
                        <div className={`p-8 ${contentBg} rounded-lg border ${borderColor} text-center ${textSecondary}`}>
                            No location data recorded yet.
                        </div>
                    ) : hasOnlyNonGeoLocations ? (
                        <div className={`p-8 ${contentBg} rounded-lg border ${borderColor} text-center ${textSecondary}`}>
                            <p>{t('developerPage.analyticsMapEarlyStage')}</p>
                        </div>
                    ) : (
                        <div className={`border ${borderColor} rounded-md overflow-hidden ${contentBg} aspect-video shadow-inner`}>
                            <style>{`
                                @keyframes analytics-marker-in {
                                    0% { opacity: 0; }
                                    100% { opacity: 1; }
                                }
                                .analytics-marker { animation: analytics-marker-in 0.5s ease-out forwards; }
                            `}</style>
                            <ComposableMap projectionConfig={{ scale: 147 }}>
                                <ZoomableGroup center={[0, 20]} zoom={1}>
                                    <Geographies geography={geoUrl}>
                                        {({ geographies }) =>
                                            geographies.map((geo) => (
                                                <Geography
                                                    key={geo.rsmKey}
                                                    geography={geo}
                                                    fill="#1f2937" // Darker Gray for land
                                                    stroke="#111827" // Even darker for borders
                                                    style={{
                                                        default: { outline: "none" },
                                                        hover: { fill: "#374151", outline: "none" }, // Slightly lighter on hover
                                                        pressed: { fill: "#4B5563", outline: "none" },
                                                    }}
                                                />
                                            ))
                                        }
                                    </Geographies>
                                    {markers && markers.map(({ key, name, coordinates, count }) => (
                                        <Marker key={key} coordinates={coordinates}>
                                            <circle
                                                className="analytics-marker"
                                                r={Math.min(4 + count * 0.5, 15)}
                                                fill="#3b82f680"
                                                stroke="#60a5fa"
                                                strokeWidth={1}
                                                filter="drop-shadow(0 0 2px rgba(96, 165, 250, 0.5))"
                                            />
                                            <title>{name}: {count} {count === 1 ? 'visit' : 'visits'}</title>
                                        </Marker>
                                    ))}
                                </ZoomableGroup>
                            </ComposableMap>
                        </div>
                    )}
                </div>

                {/* Location List Section */}
                <div className="md:w-1/3">
                    <h3 className={`text-lg font-medium mb-4 ${accentColor} flex items-center`}>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                        </svg>
                        Location Breakdown
                    </h3>
                    {(!analyticsData.locationData || analyticsData.locationData.length === 0) ? (
                        <div className={`p-6 ${contentBg} rounded-lg border ${borderColor} text-center ${textSecondary}`}>
                            No location data to display.
                        </div>
                    ) : (
                        <div className={`border ${borderColor} rounded-md p-4 ${contentBg} max-h-[400px] md:max-h-[calc(100vh_-_200px)] overflow-y-auto shadow-inner`}>
                            <ul className="space-y-2">
                                {analyticsData.locationData
                                    .sort((a, b) => b.count - a.count) // Sort by count descending
                                    .map((loc, index) => (
                                        <li key={`${loc.country}-${index}`} className={`flex justify-between items-center p-2.5 rounded-md ${index % 2 === 0 ? 'bg-gray-800/40' : 'bg-gray-800/20'}`}>
                                            <span className={`font-medium ${textPrimary}`}>
                                                {getCountryDisplayName(loc.country)}
                                            </span>
                                            <span className={`font-mono font-semibold ${accentColor} bg-blue-900/20 px-2 py-0.5 rounded-full text-sm`}>
                                                {loc.count}
                                            </span>
                                        </li>
                                    ))}
                            </ul>
                            {hasOnlyNonGeoLocations && (
                                <p className={`mt-3 pt-3 border-t ${borderColor} text-xs ${textSecondary}`}>
                                    {t('developerPage.analyticsLocationFootnote')}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

AnalyticsDisplay.propTypes = {
    analyticsData: PropTypes.shape({
        totalMessages: PropTypes.number,
        locationData: PropTypes.arrayOf(PropTypes.shape({
            country: PropTypes.string.isRequired,
            count: PropTypes.number.isRequired,
            lat: PropTypes.number,
            lng: PropTypes.number,
        }))
        // Define other expected analytics properties here
    }),
};

export default AnalyticsDisplay; 